import * as React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Button,
    Card,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField as MaterialTextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {Close, Send, Visibility} from "@mui/icons-material";
import {Field, Form, Formik} from "formik";
import {TextField} from "formik-material-ui";
import {toast} from "react-toastify";
import {TimePicker} from "@mui/x-date-pickers/TimePicker";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {CKEditor} from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-editor-classic/src/classiceditor";
import Essentials from "@ckeditor/ckeditor5-essentials/src/essentials";
import Bold from "@ckeditor/ckeditor5-basic-styles/src/bold";
import Italic from "@ckeditor/ckeditor5-basic-styles/src/italic";
import Paragraph from "@ckeditor/ckeditor5-paragraph/src/paragraph";
import Heading from "@ckeditor/ckeditor5-heading/src/heading";
import List from '@ckeditor/ckeditor5-list/src/list';
import Link from '@ckeditor/ckeditor5-link/src/link';
import AutoLink from '@ckeditor/ckeditor5-link/src/autolink';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock';
import Font from '@ckeditor/ckeditor5-font/src/font';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import TablePlugin from '@ckeditor/ckeditor5-table/src/table';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties';
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties';
import HorizontalLine from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {format, isValid, parse, parseISO} from "date-fns";
import {getEmailTemplates, previewEmail, sendTestEmail, updateFoodOrder} from "../../../API/foodOrders";
import {ERP_BASE_URL} from "../../../API/util";
import {useSelector} from "react-redux";
import {getActiveCaterers} from "../../../API/caterer";
import {NOT_APPLICABLE} from "../../Utils/constants";
import GeneralHtmlSupport from '@ckeditor/ckeditor5-html-support/src/generalhtmlsupport';
import Placeholder from "ckeditor5-placeholder";
import {DesktopDateTimePicker} from '@mui/x-date-pickers/DesktopDateTimePicker';

// CKEditor configuration with table support
const editorConfiguration = {
    plugins: [
        List, Essentials, Bold, Italic, Paragraph, Heading, Link, AutoLink,
        Placeholder,
        Indent, IndentBlock, Font, Alignment, Underline, Strikethrough,
        HorizontalLine, BlockQuote,
        TablePlugin, TableToolbar, TableProperties, TableCellProperties,
        GeneralHtmlSupport
    ],
    toolbar: [
        'heading', '|', "Placeholder", "|", 'fontColor', 'fontBackgroundColor', '|', 'outdent', 'indent', '|',
        'bold', 'italic', 'underline', 'strikethrough', '|', 'link', 'horizontalLine',
        'blockQuote', '|', 'bulletedList', 'numberedList', '|',
        'alignment:left', 'alignment:right', '|',
        'insertTable', '|', 'undo', 'redo',
        'tableColumn', '|', 'tableRow', '|', 'mergeTableCells',
    ],
    alignment: {options: ['left', 'right']},
    htmlSupport: {
        allow: [
            {name: /.*/, attributes: true, classes: true, styles: true},
            {name: 'table', attributes: ['border', 'width', 'style']},
            {name: 'thead', attributes: ['style', 'border', 'width',]},
            {name: 'tbody', attributes: ['style', 'border', 'width',]},
            {name: 'tr', attributes: ['style', 'border', 'width',]},
            {name: 'th', attributes: ['style', 'border', 'width',]},
            {name: 'td', attributes: ['style', 'border', 'width',]}
        ]
    },
    placeholderProps: {
        types: [
            'EVENT_WEEK',
            'EVENT_DAY',
            'EVENT_DATE_DMY',
            'EATING_TIME',
            'DELIVERY_TIME',
            'START_TIME',
            'END_TIME',
            'FIKA',
            'GROUP_ABBR',
            'ORDER_AMOUNT',
            'AMOUNT_SANDWICHES',
            'CATERER_FIRST_NAME',
            'LOCATION_LINE',
            'ALLERGIES_HTML',
        ],
    },
    placeholderBrackets: {
        open: "[",
        close: "]",
    },
    table: {
        contentToolbar: [],
        defaultTableWrapper: false
    }
};

const FormulaBuilder = ({name, value, setFieldValue, participants = {}, _location, error, helperText}) => {
    const [inputFormula, setInputFormula] = useState(value || "");
    const [calculation, setCalculation] = useState({result: "", message: ""});

    const variables = useMemo(
        () => [
            {key: "M_A", label: "Members Attending", value: participants?.members_attending || 0},
            {key: "M_T", label: "Members Tentative", value: participants?.members_tentative || 0},
            {key: "N_D", label: "NDs", value: participants?.nds || 0},
            {key: "M_S", label: "Speakers", value: participants?.speakers || 0},
            {key: "sum", label: "Sum of Participants", value: participants?.total || 0},
        ],
        [participants]
    );

    const operators = ["+", "-", "*", "/", "(", ")", ">", ",", "IF", "DK:", "SUM", "MAX", "MIN", "ROUND", "CEIL", "FLOOR", "ABS"];

    const parseExpression = useCallback((expr) => {
        if (!expr) return [];
        return expr
            .replace(/(\(|\)|[+\-*/>,]|IF|DK:|SUM|MAX|MIN|ROUND|CEIL|FLOOR|ABS)/g, " $1 ")
            .split(/\s+/)
            .filter((part) => part.trim());
    }, []);

    const validateFormula = useCallback(
        (expr) => {
            if (!expr) return false;
            const allowedParts = [
                ...variables.map((v) => v.key),
                ...operators,
                /\d+/, // Integers
                /\d*\.\d+/, // Decimals
            ];
            const parts = parseExpression(expr);
            return parts.every((part) =>
                allowedParts.some((allowed) =>
                    typeof allowed === "string" ? allowed === part : allowed.test?.(part)
                )
            );
        },
        [variables, parseExpression]
    );

    // Helper function to expand Excel-like functions
    const expandExcelFunctions = useCallback((input) => {
        let expression = input;

        // Expand SUM function: SUM(a, b, c) → (a + b + c)
        expression = expression.replace(/SUM\s*\(([^)]+)\)/gi, (_match, args) => {
            const argList = args.split(',').map(arg => arg.trim());
            return '(' + argList.join(' + ') + ')';
        });

        // Expand MAX function: MAX(a, b, c) → Math.max(a, b, c)
        expression = expression.replace(/MAX\s*\(([^)]+)\)/gi, (_match, args) => {
            const argList = args.split(',').map(arg => arg.trim());
            return 'Math.max(' + argList.join(', ') + ')';
        });

        // Expand MIN function: MIN(a, b, c) → Math.min(a, b, c)
        expression = expression.replace(/MIN\s*\(([^)]+)\)/gi, (_match, args) => {
            const argList = args.split(',').map(arg => arg.trim());
            return 'Math.min(' + argList.join(', ') + ')';
        });

        // Expand ROUND function: ROUND(value, decimals) → Math.round(value)
        expression = expression.replace(/ROUND\s*\(([^)]+)\)/gi, (_match, args) => {
            const argList = args.split(',').map(arg => arg.trim());
            const value = argList[0];
            const decimals = argList[1] ? parseInt(argList[1]) : 0;
            return decimals === 0 ? `Math.round(${value})` : `Math.round(${value} * Math.pow(10, ${decimals})) / Math.pow(10, ${decimals})`;
        });

        // Expand CEIL function: CEIL(value) → Math.ceil(value)
        expression = expression.replace(/CEIL\s*\(([^)]+)\)/gi, 'Math.ceil($1)');

        // Expand FLOOR function: FLOOR(value) → Math.floor(value)
        expression = expression.replace(/FLOOR\s*\(([^)]+)\)/gi, 'Math.floor($1)');

        // Expand ABS function: ABS(value) → Math.abs(value)
        expression = expression.replace(/ABS\s*\(([^)]+)\)/gi, 'Math.abs($1)');

        // Expand IF function: IF(condition, true_value, false_value) → (condition ? true_value : false_value)
        expression = expression.replace(/IF\s*\(([^)]+)\)/gi, (match, args) => {
            const parts = [];
            let current = '';
            let depth = 0;
            let inQuotes = false;

            for (let i = 0; i < args.length; i++) {
                const char = args[i];
                if (char === '"' || char === "'") {
                    inQuotes = !inQuotes;
                } else if (!inQuotes) {
                    if (char === '(') depth++;
                    else if (char === ')') depth--;
                    else if (char === ',' && depth === 0) {
                        parts.push(current.trim());
                        current = '';
                        continue;
                    }
                }
                current += char;
            }
            parts.push(current.trim());

            if (parts.length === 3) {
                return `(${parts[0]} ? ${parts[1]} : ${parts[2]})`;
            }
            return match; // Return original if not 3 parts
        });

        return expression;
    }, []);

    const calculateResult = useCallback(() => {
        try {
            if (!inputFormula) {
                toast.error("Please enter a formula");
                setCalculation({result: "", message: ""});
                return;
            }

            if (!validateFormula(inputFormula)) {
                toast.error("Invalid formula: allowed variables (M_A, M_T, N_D, M_S, sum), operators (+,-,*,/,>,,,(,),IF,DK:), functions (SUM,MAX,MIN,ROUND,CEIL,FLOOR,ABS), and numbers");
                setCalculation({result: "Invalid formula", message: ""});
                return;
            }

            let expression = inputFormula;

            // Expand Excel-like functions
            expression = expandExcelFunctions(expression);

            variables.forEach((v) => {
                const variablePattern = new RegExp(`\\b${v.key}\\b`, "g");
                expression = expression.replace(variablePattern, v.value);
            });

            // Safety check - allow Excel-like functions and mathematical operations
            if (!/^[0-9\.\s\+\-\*\/\(\)\?\:\,\<\>\=\!\&\|a-zA-Z_]+$/.test(expression)) {
                throw new Error("Unsafe characters detected");
            }

            const result = Function(`"use strict"; return (${expression})`)();
            const finalResult = Math.max(0, Math.round(result));

            setCalculation({result: finalResult, message: "Formula calculated successfully"});
            setFieldValue(name, inputFormula);
        } catch (e) {
            setCalculation({result: "Invalid formula", message: ""});
        }
    }, [inputFormula, variables, validateFormula, setFieldValue, name, expandExcelFunctions]);

    // Sync Formik field with inputFormula changes
    useEffect(() => {
        setFieldValue(name, inputFormula);
    }, [inputFormula, name, setFieldValue]);

    // Validate and calculate on blur or button click
    const handleBlur = () => {
        calculateResult();
    };

    return (
        <Box sx={{border: `1px solid ${error ? "red" : "rgba(0,0,0,0.23)"}`, borderRadius: "4px", p: 2}}>
            <Typography variant="subtitle2">
                Order Calculation Formula*{" "}
                {helperText && <Typography variant="caption" color="textSecondary">({helperText})</Typography>}
            </Typography>

            {/* Variables Table */}
            <Table size="small" sx={{mb: 2}}>
                <TableHead>
                    <TableRow>
                        <TableCell>Variable</TableCell>
                        <TableCell>Label</TableCell>
                        <TableCell>Value</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {variables.map((v) => (
                        <TableRow key={v.key}>
                            <TableCell>{v.key}</TableCell>
                            <TableCell>{v.label}</TableCell>
                            <TableCell>{v.value}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {/* Formula Input */}
            <Box sx={{mb: 2}}>
                <MaterialTextField
                    label="Enter Formula"
                    placeholder="e.g., (M_A + (M_T/2) + N_D + M_S)"
                    value={inputFormula}
                    onChange={(e) => setInputFormula(e.target.value)}
                    onBlur={handleBlur}
                    fullWidth
                    size="small"
                    error={!!error}
                    helperText={error}
                />
            </Box>

            {/* Calculate Button */}
            <Box sx={{mb: 2}}>
                <Button variant="outlined" onClick={calculateResult}>
                    Calculate
                </Button>
            </Box>

            {/* Result Section */}
            <Box sx={{mt: 2}}>
                {calculation.result && (
                    <Typography variant="subtitle2"
                                color={calculation.result === "Invalid formula" ? "error" : "textPrimary"}>
                        Result: {calculation.result}
                    </Typography>
                )}
                {calculation.message && (
                    <Typography variant="subtitle2" color="textSecondary">
                        {calculation.message}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

// Reusable section for Pre/Final templates
const TemplateFormSection = ({
                                 title,
                                 subjectLabel,
                                 bodyLabel,
                                 subjectValue,
                                 bodyValue,
                                 onSubjectChange,
                                 onBodyChange,
                                 onTestEmail,
                                 onPreviewEmail,
                             }) => (
    <Box>
        <Grid item xs={12}>
            <Typography variant="subtitle1" my={2}>{title}</Typography>
        </Grid>
        <Grid item xs={12}>
            <MaterialTextField
                label={subjectLabel}
                value={subjectValue}
                onChange={(e) => onSubjectChange(e.target.value)}
                fullWidth
                size="small"
            />
        </Grid>
        <Grid item xs={12}>
            <Typography variant="subtitle2">{bodyLabel}</Typography>
            <CKEditor
                editor={ClassicEditor}
                config={editorConfiguration}
                data={bodyValue}
                onReady={_editor => {
                }}
                onChange={(_event, editor) => {
                    const data = editor.getData();
                    onBodyChange(data);
                }}
                style={{height: "300px", marginBottom: "60px"}}
            />
        </Grid>
        <Grid item xs={12} mt={2}>
            <Box sx={{display: "flex", gap: 2}}>
                <Tooltip title="Send a test email with the previewed content" placement="top">
                    <Button
                        variant="contained"
                        startIcon={<Send/>}
                        onClick={onTestEmail}
                        aria-label={`Send Test ${title} Email`}
                    >
                        Test {title} Email
                    </Button>
                </Tooltip>
                <Tooltip title="Preview template content" placement="top">
                    <Button
                        variant="contained"
                        startIcon={<Visibility sx={{color: "white"}}/>}
                        onClick={onPreviewEmail}
                        aria-label={`Preview ${title} Email`}
                    >
                        Preview {title}
                    </Button>
                </Tooltip>
            </Box>
        </Grid>
    </Box>
);

// Main Component
const ManageFoodOrderModal = (props) => {
    const userEmail = useSelector((state) => state.account.data.user.user_details.user_email);
    const {open, data, handleClose} = props;
    const formRef = useRef(null);
    const [_, setFoodOrder] = useState(null);
    const [caterers, setCaterers] = useState([]);
    const [prePreviewContent, setPrePreviewContent] = useState("");
    const [finalPreviewContent, setFinalPreviewContent] = useState("");
    const [testEmailOpen, setTestEmailOpen] = useState(false);
    const [testEmail, setTestEmail] = useState(userEmail || "");
    const [emailType, setEmailType] = useState(""); // New state to track email type (pre or final)
    const [emailTemplates, setEmailTemplates] = useState({pre_templates: [], final_templates: []});
    const [_selectedPreTemplate, setSelectedPreTemplate] = useState(null);
    const [_selectedFinalTemplate, setSelectedFinalTemplate] = useState(null);
    const [expanded, setExpanded] = useState('pre');
    const [expandedFinal, setExpandedFinal] = useState('final');
    const emailPreviewRef = useRef(null);
    const handleChange =
        (panel) => (_event, isExpanded) => {
            if (panel === 'pre') {
                setExpanded(isExpanded ? panel : false);
            } else {
                setExpandedFinal(isExpanded ? panel : false);
            }
        };

    useEffect(() => {
        if (open) {
            data.pre_order_email_trigger_date = parse(data.pre_order_email_trigger_date, "dd/MM/yyyy HH:mm", new Date())
            data.final_order_email_trigger_date = parse(data.final_order_email_trigger_date, "dd/MM/yyyy HH:mm", new Date())

            setFoodOrder(data);
            getActiveCaterers({viewArchived: true})
                .then((res) => setCaterers(res.data.main))
                .catch(() => toast.error("Failed to load caterers"));
            getEmailTemplates()
                .then((res) => setEmailTemplates(res))
                .catch(() => toast.error("Failed to load email templates"));
        }
    }, [data, open]);

    const handlePreviewEmail = (type) => {
        const values = formRef.current?.values;
        if (!values) return;

        const payload = {
            caterer_id: values.caterer_id,
            food_order_id: props.data?.food_order_id,
            event_date: values.event_date,
            location: values.location,
            country: values.country,
            fika_time: values.fika_time,
            fika: values.fika,
            end_time: values.end_time ? format(values.end_time, "HH:mm") : "",
            group_name: values.group_name,
            delivery_time: values.delivery_time ? format(values.delivery_time, "HH:mm") : "",
            sandwiches_amount: values.sandwiches_amount,
            order_formula: values.order_formula,
            participants: values.participants,
            comment: values.comment,
        };

        if (type === "pre") {
            payload.email_template_id = values.pre_email_template_id;
            payload.email_subject = values.pre_order_email_subject;
            payload.email_body = values.pre_order_email_body;
        } else if (type === "final") {
            payload.email_template_id = values.final_email_template_id;
            payload.email_subject = values.final_order_email_subject;
            payload.email_body = values.final_order_email_body;
        }

        if (!payload.email_subject || !payload.email_body) {
            toast.error(`Please select a ${type === "pre" ? "Pre-Order" : "Final Order"} template and fill in subject and body`);
            return;
        }

        previewEmail(payload)
            .then((res) => {
                if (type === "pre") {
                    setPrePreviewContent(res.html);
                    setFinalPreviewContent("");
                } else {
                    setFinalPreviewContent(res.html);
                    setPrePreviewContent("");
                }

                emailPreviewRef.current.scrollIntoView({behavior: "smooth"});
            })
            .catch(() => toast.error(`Failed to preview ${type === "pre" ? "Pre-Order" : "Final Order"} email`));
    };

    const handleSendTestEmail = () => {
        if (!testEmail || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(testEmail)) {
            toast.error("Invalid test email address");
            return;
        }
        const values = formRef.current?.values;
        if (!values) {
            toast.error("Form data is not available");
            return;
        }

        let email_subject, email_body;
        if (emailType === "pre") {
            email_subject = values.pre_order_email_subject;
            email_body = values.pre_order_email_body;
        } else if (emailType === "final") {
            email_subject = values.final_order_email_subject;
            email_body = values.final_order_email_body;
        } else {
            toast.error(`No ${emailType === "pre" ? "Pre-Order" : "Final Order"} email preview content available`);
            return;
        }

        if (!email_subject || !email_body) {
            toast.error("Email subject and body are required");
            return;
        }

        sendTestEmail({
            id: props.data?.food_order_id || null,
            email: testEmail,
            email_subject,
            email_body,
            values: values
        }).then((res) => {
            if (emailType === "pre") {
                setPrePreviewContent(res.html);
                setFinalPreviewContent("");
            } else {
                setFinalPreviewContent(res.html);
                setPrePreviewContent("");
            }
            toast.success(`${emailType === "pre" ? "Pre-Order" : "Final Order"} test email sent`)

        }).catch(() => toast.error(`Failed to send ${emailType === "pre" ? "Pre-Order" : "Final Order"} test email`));

        setTestEmailOpen(false);
        setTestEmail("");
        setEmailType("");
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Dialog open={open} scroll="paper" fullWidth maxWidth="xxl"
                    sx={{"& .MuiDialog-paper": {maxHeight: "90vh"}}}>
                <DialogTitle sx={{backgroundColor: "#1565c0", color: "white"}}>
                    Edit Food Order
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => handleClose(false)}
                    sx={(theme) => ({
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    <Close/>
                </IconButton>
                <DialogContent sx={{padding: "14px", backgroundColor: "#F7F7F7"}}>
                    <Formik
                        innerRef={formRef}
                        enableReinitialize
                        initialValues={{
                            group_name: data?.order_config?.group?.abbr || "",
                            location: data?.order_config?.location?.code || "",
                            country: data?.event?.event_country || "",
                            address: data?.order_config?.location?.address || "",
                            city: data?.order_config?.location?.city || "",
                            zip: data?.order_config?.location?.zip || "",
                            fika: data?.fika && data?.fika != NOT_APPLICABLE ? true : false,
                            fika_time: data?.fika && data?.fika_time && isValid(parseISO(`${format(parseISO(data.order_date), "yyyy-MM-dd")}T${data.fika_time}`))
                                ? parseISO(`${format(parseISO(data.order_date), "yyyy-MM-dd")}T${data.fika_time}`)
                                : "",
                            end_time: data?.end_time && isValid(parseISO(`${format(parseISO(data.order_date), "yyyy-MM-dd")}T${data.end_time}`))
                                ? parseISO(`${format(parseISO(data.order_date), "yyyy-MM-dd")}T${data.end_time}`)
                                : "",
                            event_date: data?.event_start,
                            delivery_time: data?.delivery_time && data?.order_date && isValid(parseISO(`${format(parseISO(data.order_date), "yyyy-MM-dd")}T${data.delivery_time}`))
                                ? parseISO(`${format(parseISO(data.order_date), "yyyy-MM-dd")}T${data.delivery_time}`)
                                : "",
                            sandwiches_amount: data?.sandwich_amount ?? 0,
                            speaker: data?.speaker_count ?? 0,
                            order_formula: data?.order_config?.rules?.formula || "",
                            comment: data?.comments || "",
                            pre_order_email_trigger_date: data?.pre_order_email_trigger_date,
                            final_order_email_trigger_date: data?.final_order_email_trigger_date,
                            email_writeup: data?.email_body || "",
                            caterer_email: data?.caterer_email || caterers.find((c) => c.default_for_location === data?.order_config?.location?.code)?.email || "",
                            pre_order_ready: data?.pre_order_email_status === "Ready" || false,
                            is_pre_order_email_content_ready: data?.is_pre_order_email_content_ready || false,
                            is_final_order_email_content_ready: data?.is_final_order_email_content_ready || false,
                            participants: {
                                total: data?.order_config?.stats?.TOTAL ?? 0,
                                members_attending: data?.order_config?.stats?.M_A ?? 0,
                                members_tentative: data?.order_config?.stats?.M_T ?? 0,
                                nds: data?.order_config?.stats?.N_D ?? 0,
                                speakers: data?.order_config?.stats?.M_S ?? 0,
                                defacto: data?.order_config?.stats?.DEFACTO ?? 0,
                                difference: data?.order_config?.stats?.DIFF ?? 0,
                            },
                            shift_type: data?.shift_type || "morning",
                            caterer_id: data?.caterer_id,
                            pre_email_template_id: data?.pre_email_template_id || "",
                            final_email_template_id: data?.final_email_template_id || "",
                            pre_order_email_subject: data?.pre_order_email_subject || "",
                            pre_order_email_body: data?.pre_order_email_body || "",
                            final_order_email_subject: data?.final_order_email_subject || "",
                            final_order_email_body: data?.final_order_email_body || "",
                        }}
                        validate={(values) => {
                            const errors = {};
                            if (!values.order_formula) {
                                errors.order_formula = "Required";
                            } else {
                                // Reuse validateFormula logic
                                const allowedParts = [
                                    "M_A",
                                    "M_T",
                                    "N_D",
                                    "M_S",
                                    "sum", // Added to allow 'sum' variable
                                    "IF", // Added to allow IF keyword
                                    "DK:", // Added to allow country prefix
                                    "SUM", "MAX", "MIN", "ROUND", "CEIL", "FLOOR", "ABS", // Added Excel functions
                                    "+", "-", "*", "/", "(", ")", ">", ",", // Added '>' and ',' as operators
                                    /\d+/, // Allow integers
                                    /\d*\.\d+/ // Allow decimals
                                ];

                                const parts = values.order_formula
                                    .replace(/(\(|\)|[+\-*/>,]|DK:|IF|SUM|MAX|MIN|ROUND|CEIL|FLOOR|ABS)/g, " $1 ") // Updated to include Excel functions
                                    .split(/\s+/)
                                    .filter((part) => part.trim());

                                const isValidFormula = parts.every((part) =>
                                    allowedParts.some((allowed) =>
                                        typeof allowed === "string" ? allowed === part : allowed.test?.(part)
                                    )
                                );

                                if (!isValidFormula) {
                                    errors.order_formula = "Invalid formula: allowed variables (M_A, M_T, N_D, M_S, sum), operators (+, -, *, /, >, ,, (, )), functions (SUM,MAX,MIN,ROUND,CEIL,FLOOR,ABS), IF, DK:, and numbers";
                                }
                            }

                            return errors;
                        }}
                        onSubmit={(values, {setSubmitting}) => {
                            const action = data?.food_order_id ? updateFoodOrder(data.food_order_id, values) : null;
                            action
                                .then((res) => {
                                    toast.success(res.message || "Food order saved successfully");
                                    handleClose(true);
                                    setSubmitting(false);
                                })
                                .catch((err) => {
                                    toast.error(err.message || "Something went wrong");
                                    setSubmitting(false);
                                });
                        }}
                    >
                        {({values, isSubmitting, setFieldValue, submitCount, errors}) => (
                            <Form>
                                <Card sx={{padding: "20px", mb: 2}}>
                                    <Typography variant="h6" gutterBottom>
                                        Event Details (Non-Editable)
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <MaterialTextField label="Group Name/Abbr" value={values.group_name}
                                                               fullWidth disabled/>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <MaterialTextField label="Event Date" value={values.event_date} fullWidth
                                                               disabled/>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <MaterialTextField label="Location"
                                                               value={`${values.address}, ${values.city}, ${values.zip}`}
                                                               fullWidth disabled/>
                                        </Grid>
                                    </Grid>
                                </Card>

                                <Card sx={{padding: "20px", mb: 2}}>
                                    <Typography variant="h6" gutterBottom>
                                        Food Order Details
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {values.location && values.location.startsWith("SE/") && (
                                            <Grid item xs={12} sm={6}>
                                                <Field
                                                    name="fika"
                                                    render={({field, form}) => (
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={field.value || false}
                                                                    onChange={(e) => {
                                                                        form.setFieldValue(field.name, e.target.checked);
                                                                    }}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label="FIKA (SE/STO)"
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        )}
                                        {values.location && values.location.startsWith("SE/") && values.fika && (
                                            <Grid item xs={12} sm={6}>
                                                <TimePicker
                                                    name="fika_time"
                                                    label="FIKA Time"
                                                    value={values.fika_time}
                                                    ampm={false}
                                                    onChange={(newValue) => {
                                                        if (!newValue || !isValid(newValue)) {
                                                            setFieldValue("fika_time", null);
                                                            toast.error("Invalid time selected");
                                                            return;
                                                        }
                                                        const combinedDateTime = new Date(newValue);
                                                        combinedDateTime.setHours(newValue.getHours(), newValue.getMinutes(), 0, 0);
                                                        if (isValid(combinedDateTime)) {
                                                            setFieldValue("fika_time", combinedDateTime);
                                                        } else {
                                                            toast.error("Invalid delivery time");
                                                            setFieldValue("fika_time", null);
                                                        }
                                                    }}
                                                    renderInput={(params) => (
                                                        <MaterialTextField fullWidth size="small" {...params} />
                                                    )}
                                                />
                                            </Grid>
                                        )}
                                        {values.location && values.location.startsWith("SE/") && (
                                            <Grid item xs={12} sm={6}>
                                                <TimePicker
                                                    label="End Time (SE/STO)"
                                                    value={values.end_time}
                                                    disabled={true}
                                                    onChange={(newValue) => {
                                                        if (!newValue || !isValid(newValue)) {
                                                            setFieldValue("end_time", null);
                                                            toast.error("Invalid time selected");
                                                            return;
                                                        }
                                                        const combinedDateTime = new Date(newValue);
                                                        combinedDateTime.setHours(newValue.getHours(), newValue.getMinutes(), 0, 0);
                                                        if (isValid(combinedDateTime)) {
                                                            setFieldValue("end_time", combinedDateTime);
                                                        } else {
                                                            toast.error("Invalid delivery time");
                                                            setFieldValue("end_time", null);
                                                        }
                                                    }}
                                                    ampm={false}
                                                    renderInput={(params) => (
                                                        <MaterialTextField fullWidth size="small" {...params} />
                                                    )}
                                                />
                                            </Grid>
                                        )}
                                        {(values.city.toLowerCase() !== "aalborg" && values.country.toLowerCase() !== "sweden") && (
                                            <Grid item xs={12} sm={6}>
                                                <TimePicker
                                                    label="Delivery Time"
                                                    value={values.delivery_time}
                                                    disabled={true}
                                                    ampm={false}
                                                    renderInput={(params) => (
                                                        <MaterialTextField
                                                            {...params}
                                                            fullWidth
                                                            size="small"
                                                            required={false}
                                                            error={false}
                                                            helperText=""
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        )}
                                        {values.city.toLowerCase() === "hellerup" && (
                                            <Grid item xs={12} sm={6}>
                                                <Field
                                                    name="sandwiches_amount"
                                                    label="Amount of Sandwiches/Snacks (CPH)"
                                                    component={TextField}
                                                    disabled={true}
                                                    fullWidth
                                                    size="small"
                                                />
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            <FormulaBuilder
                                                name="order_formula"
                                                value={values.order_formula}
                                                setFieldValue={setFieldValue}
                                                participants={values.participants}
                                                location={values.location}
                                                error={submitCount > 0 && errors.order_formula}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1">Comment</Typography>
                                            <CKEditor
                                                editor={ClassicEditor}
                                                config={editorConfiguration}
                                                data={values.comment}
                                                onReady={_editor => {
                                                }}
                                                onChange={(_event, editor) => {
                                                    const data = editor.getData();
                                                    setFieldValue("comment", data);
                                                }}
                                                style={{height: "150px", marginBottom: "20px"}}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                                {(data?.isParentOrder === true || data?.isSingleOrder === true) && (
                                    <Grid>
                                        <Card sx={{padding: "20px", mb: 2}}>
                                            <Typography variant="h6" gutterBottom>
                                                Trigger Dates
                                            </Typography>
                                            <Grid container spacing={2}>
                                                {values.location.toLowerCase() !== "aarhus" && (
                                                    <Grid item xs={12} sm={6}>
                                                        <DesktopDateTimePicker
                                                            label="Pre-Order Trigger Date"
                                                            inputFormat="dd/MM/yyyy HH:mm"
                                                            format="dd/MM/yyyy HH:mm"
                                                            ampm={false}
                                                            value={values.pre_order_email_trigger_date ? values.pre_order_email_trigger_date : null}
                                                            onChange={(newValue) => {
                                                                setFieldValue("pre_order_email_trigger_date", newValue && isValid(newValue) ? format(newValue, "yyyy-MM-dd HH:mm") : "");
                                                            }}
                                                            renderInput={(params) => <MaterialTextField fullWidth
                                                                                                        size="small" {...params}
                                                                                                        required={false}
                                                                                                        error={false}
                                                                                                        helperText=""/>}
                                                        />
                                                    </Grid>
                                                )}
                                                <Grid item xs={12} sm={6}>
                                                    <DesktopDateTimePicker
                                                        label="Final Order Trigger Date"
                                                        inputFormat="dd/MM/yyyy HH:mm"
                                                        format="dd/MM/yyyy HH:mm"
                                                        ampm={false}
                                                        value={values.final_order_email_trigger_date ? values.final_order_email_trigger_date : null}
                                                        onChange={(newValue) => {
                                                            setFieldValue("final_order_email_trigger_date", newValue && isValid(newValue) ? format(newValue, "yyyy-MM-dd HH:mm") : "");
                                                        }}
                                                        renderInput={(params) => <MaterialTextField fullWidth
                                                                                                    size="small" {...params}
                                                                                                    required={false}
                                                                                                    error={false}
                                                                                                    helperText=""/>}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Card>

                                        <Card sx={{padding: "20px", mb: 2}}>
                                            <Typography variant="h6" gutterBottom>
                                                Email Configuration
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <Autocomplete
                                                        options={caterers}
                                                        getOptionLabel={(option) => option.email}
                                                        disabled={true}
                                                        value={caterers.find((c) => c.caterer_id === values.caterer_id) || null}
                                                        onChange={(_e, newValue) => setFieldValue("caterer_id", newValue?.caterer_id || "")}
                                                        renderInput={(params) => (
                                                            <MaterialTextField
                                                                {...params}
                                                                label="Caterer Recipient Email*"
                                                                fullWidth
                                                                size="small"
                                                                error={submitCount > 0 && errors.caterer_id}
                                                                helperText={submitCount > 0 && errors.caterer_id}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid container spacing={3} mt={2} px={2}>
                                                    {/* Pre-Order Section */}
                                                    {(values.location.toLowerCase() !== "aarhus" && values.city.toLowerCase() !== "aarhus") && (
                                                        <Grid item xs={12} md={12}>
                                                            <Accordion
                                                                expanded={expanded === 'pre'}
                                                                onChange={handleChange('pre')}
                                                            >
                                                                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                                                    Pre-Order Email Template
                                                                </AccordionSummary>
                                                                <AccordionDetails>
                                                                    <FormControl fullWidth size="small">
                                                                        <InputLabel>Pre-Order Email
                                                                                    Template</InputLabel>
                                                                        <Select
                                                                            value={values.pre_email_template_id || ""}
                                                                            onChange={(e) => {
                                                                                const template = emailTemplates?.pre_templates?.find(
                                                                                    (t) => t.template_id === e.target.value
                                                                                );
                                                                                setSelectedPreTemplate(template);
                                                                                setFieldValue("pre_email_template_id", e.target.value);
                                                                                setFieldValue("pre_order_email_subject", template?.template_subject || "");
                                                                                setFieldValue("pre_order_email_body", template?.template_body || "");
                                                                            }}
                                                                        >
                                                                            <MenuItem value="">None</MenuItem>
                                                                            {emailTemplates?.pre_templates?.map((template) => (
                                                                                <MenuItem key={template.template_id}
                                                                                          value={template.template_id}>
                                                                                    {template.template_name}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>

                                                                    <Box mt={2}>
                                                                        <TemplateFormSection
                                                                            title="Pre-Order Template"
                                                                            subjectLabel="Pre-Order Email Subject"
                                                                            bodyLabel="Pre-Order Email Body"
                                                                            subjectValue={values.pre_order_email_subject}
                                                                            bodyValue={values.pre_order_email_body}
                                                                            onSubjectChange={(val) => setFieldValue("pre_order_email_subject", val)}
                                                                            onBodyChange={(val) => setFieldValue("pre_order_email_body", val)}
                                                                            onTestEmail={() => {
                                                                                handlePreviewEmail("pre");
                                                                                setEmailType("pre");
                                                                                setTestEmailOpen(true);
                                                                            }}
                                                                            onPreviewEmail={() => handlePreviewEmail("pre")}
                                                                        />
                                                                    </Box>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        </Grid>
                                                    )}

                                                    {values.location.toLowerCase() !== "aarhus" && (
                                                        <Grid item xs={12} sm={6}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={values.is_pre_order_email_content_ready}
                                                                        onChange={(e) => setFieldValue("is_pre_order_email_content_ready", e.target.checked)}
                                                                    />
                                                                }
                                                                label="Pre Order Email Content Ready"
                                                            />
                                                        </Grid>
                                                    )}
                                                    {/* Final-Order Section */}
                                                    <Grid item xs={12} md={12}>
                                                        <Accordion
                                                            expanded={expandedFinal === 'final'}
                                                            onChange={handleChange('final')}
                                                        >
                                                            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                                                Final Order Email Template
                                                            </AccordionSummary>
                                                            <AccordionDetails>
                                                                <FormControl fullWidth size="small">
                                                                    <InputLabel>Final Order Email Template</InputLabel>
                                                                    <Select
                                                                        value={values.final_email_template_id || ""}
                                                                        onChange={(e) => {
                                                                            const template = emailTemplates?.final_templates?.find(
                                                                                (t) => t.template_id === e.target.value
                                                                            );
                                                                            setSelectedFinalTemplate(template);
                                                                            setFieldValue("final_email_template_id", e.target.value);
                                                                            setFieldValue("final_order_email_subject", template?.template_subject || "");
                                                                            setFieldValue("final_order_email_body", template?.template_body || "");
                                                                        }}
                                                                    >
                                                                        <MenuItem value="">None</MenuItem>
                                                                        {emailTemplates?.final_templates?.map((template) => (
                                                                            <MenuItem key={template.template_id}
                                                                                      value={template.template_id}>
                                                                                {template.template_name}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>

                                                                <Box mt={2}>
                                                                    <TemplateFormSection
                                                                        title="Final-Order Template"
                                                                        subjectLabel="Final Order Email Subject"
                                                                        bodyLabel="Final Order Email Body"
                                                                        subjectValue={values.final_order_email_subject}
                                                                        bodyValue={values.final_order_email_body}
                                                                        onSubjectChange={(val) => setFieldValue("final_order_email_subject", val)}
                                                                        onBodyChange={(val) => setFieldValue("final_order_email_body", val)}
                                                                        onTestEmail={() => {
                                                                            handlePreviewEmail("final");
                                                                            setEmailType("final");
                                                                            setTestEmailOpen(true);
                                                                        }}
                                                                        onPreviewEmail={() => handlePreviewEmail("final")}
                                                                    />
                                                                </Box>
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    </Grid>
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={values.is_final_order_email_content_ready}
                                                                onChange={(e) => setFieldValue("is_final_order_email_content_ready", e.target.checked)}
                                                            />
                                                        }
                                                        label="Final Order Email Content Ready"
                                                    />
                                                </Grid>

                                                {/* Email Preview Section */}
                                                {(prePreviewContent || finalPreviewContent) && (
                                                    <Grid item xs={12} ref={emailPreviewRef}
                                                          className={"email-preview-container"}>
                                                        <Typography variant="h6" gutterBottom>
                                                            Email Preview
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                border: '1px solid #ccc',
                                                                borderRadius: '4px',
                                                                padding: '16px',
                                                                backgroundColor: '#fff',
                                                                maxHeight: '400px',
                                                                overflow: 'auto',
                                                                '& table': {
                                                                    borderCollapse: 'collapse !important',
                                                                    width: '100% !important',
                                                                    border: '1px solid #000 !important'
                                                                },
                                                                '& th, & td': {
                                                                    border: '1px solid #000 !important',
                                                                    padding: '6px !important'
                                                                },
                                                                '& th': {
                                                                    fontWeight: 'bold !important',
                                                                    color: '#000 !important'
                                                                }
                                                            }}
                                                        >
                                                            <div
                                                                dangerouslySetInnerHTML={{
                                                                    __html: prePreviewContent || finalPreviewContent
                                                                }}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Card>
                                    </Grid>
                                )}
                                <Grid container spacing={2} justifyContent="space-between">
                                    <Grid item>
                                        <a target="_blank" href={`${ERP_BASE_URL}/#/event/${data?.event_id}`}>
                                            View Event Details
                                        </a>
                                    </Grid>
                                    <Grid item>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            disabled={isSubmitting}
                                            sx={{mr: 2}}
                                        >
                                            Save Information
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            onClick={() => handleClose(false)}
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>

            <Dialog open={testEmailOpen} onClose={() => setTestEmailOpen(false)}>
                <DialogTitle>Send Test {emailType === "pre" ? "Pre-Order" : "Final Order"} Email</DialogTitle>
                <DialogContent>
                    <MaterialTextField
                        label="Recipient Email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        fullWidth
                        size="small"
                        sx={{mt: 2}}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTestEmailOpen(false)}>Cancel</Button>
                    <Button onClick={handleSendTestEmail} color="primary">
                        Send
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default ManageFoodOrderModal;